import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { 
  Music,
  Zap, 
  FileText, 
  DollarSign, 
  Upload,
  Heart,
  CheckCircle,
  Tag,
  Star,
  Users,
  Lightbulb,
  Download,
  Camera,
  Archive,
  Clock,
  HardDrive,
  AlertCircle,
  ExternalLink,
  Mail
} from 'lucide-react';

const SequenceGuide = () => {
  return (
    <>
      <Helmet>
        <title>🎨 Light Hunt Sequence Creator Guide - Empowering Artists to Light Up the Holidays</title>
        <meta name="description" content="Complete guide for holiday light sequence creators. Learn about xLights, Light-O-Rama, file formats, monetization, and best practices for creating amazing synchronized sequences." />
        <meta name="keywords" content="xLights sequences, Light-O-Rama, sequence creator guide, holiday light sequences, synchronized lights, light show creation, sequence monetization, xseq files, LOR sequences" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow container mx-auto px-4 py-8 max-w-4xl">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                🎨 Light Hunt Sequence Creator Guide
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Empowering Artists to Light Up the Holidays
              </p>
              <div className="w-16 h-px bg-linear-to-r from-transparent via-primary to-transparent mx-auto"></div>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Welcome to the Light Hunt Creator community! Whether you're an xLights wizard, a Light‑O‑Rama legend, or just getting started syncing lights to music, this guide is your go-to resource for sharing your creativity with the world.
              </p>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-medium">
                Let's help you turn your sequences into something magical—for you and for fellow display owners.
              </p>
            </div>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Recommended Tools Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">🔧 Recommended Tools</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  To create and upload a sequence, we recommend using one of the following:
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* xLights */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">xLights</h3>
                  </div>
                  <p className="text-muted-foreground mb-3">
                    Open-source and widely supported, perfect for pixel-based shows.
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Export your sequences in <strong>.xseq</strong> or <strong>.xml</strong> formats</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Perfect for RGB pixel displays and complex matrix effects</span>
                    </li>
                  </ul>
                </div>

                {/* Light-O-Rama */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Music className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Light‑O‑Rama (LOR)</h3>
                  </div>
                  <p className="text-muted-foreground mb-3">
                    Great for AC lighting and hybrid shows.
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Use <strong>.lms</strong>, <strong>.las</strong>, or <strong>.seq</strong> file formats</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Excellent for traditional AC lighting setups</span>
                    </li>
                  </ul>
                </div>

                {/* ZIP Packaging */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow md:col-span-2">
                  <div className="flex items-center space-x-3 mb-4">
                    <Archive className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">ZIP your work</h3>
                  </div>
                  <p className="text-muted-foreground">
                    <strong>Always package your sequence file and any accompanying audio or model files into a .zip archive before uploading.</strong> This ensures everything stays together and makes it easy for others to use your work.
                  </p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* File Formats & Requirements Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <FileText className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">📁 Supported File Formats & Requirements</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  When you upload to Light Hunt, we accept the following:
                </p>
              </div>

              {/* File Format Table */}
              <div className="bg-card rounded-lg border overflow-hidden">
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Supported File Types</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-4 font-medium">File Type</th>
                          <th className="text-left py-2 px-4 font-medium">Format</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2 px-4">xLights Sequence</td>
                          <td className="py-2 px-4 font-mono text-sm">.xseq, .xml</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 px-4">LOR Sequence</td>
                          <td className="py-2 px-4 font-mono text-sm">.lms, .seq, .las</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-4">Audio/Assets</td>
                          <td className="py-2 px-4">Include in .zip</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="grid gap-6 md:grid-cols-3">
                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">Duration</h4>
                  </div>
                  <p className="text-muted-foreground">Sequences must be between <strong>30 seconds and 10 minutes</strong>.</p>
                </div>

                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-3">
                    <HardDrive className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">File Size</h4>
                  </div>
                  <p className="text-muted-foreground">File size limit: <strong>500MB max</strong> per upload.</p>
                </div>

                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-3">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    <h4 className="font-semibold">Audio Notice</h4>
                  </div>
                  <p className="text-muted-foreground">Please avoid copyrighted audio—if included, note that it is the user's responsibility to source the music legally.</p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Monetization Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <DollarSign className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">💸 Monetization & Pricing</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Light Hunt allows you to monetize your hard work and talent.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="p-6 rounded-lg border bg-card">
                  <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                    <Upload className="h-5 w-5 text-primary" />
                    <span>How It Works</span>
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Upload your sequence → You choose the price</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Typical pricing range: <strong>$25–$50</strong></span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>You keep a majority of each sale via our royalty system (details available upon account setup)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Free sequences are encouraged too—help others and build your reputation!</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6 rounded-lg border bg-card bg-primary/5 border-primary/20">
                  <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                    <Lightbulb className="h-5 w-5 text-primary" />
                    <span>💡 Pro Tip</span>
                  </h3>
                  <p className="text-muted-foreground">
                    Quality sequences with clean previews, proper tags, and detailed descriptions often sell more and build a loyal fanbase.
                  </p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Upload Tips Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Star className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">🧰 Tips for Successful Uploads</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Here's how to stand out and help others find your work:
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {/* Preview Quality */}
                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-4">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <Camera className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Preview Quality</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Always include a preview video or screenshot of your sequence in action</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Watermark your video if you'd like to protect your work</span>
                    </li>
                  </ul>
                </div>

                {/* Use Tags Wisely */}
                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-4">
                    <Tag className="h-6 w-6 text-blue-500" />
                    <h3 className="text-lg font-semibold">🏷 Use Tags Wisely</h3>
                  </div>
                  <p className="text-muted-foreground mb-3">Include keywords like:</p>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Halloween, Christmas, Pixels, AC, Singing Faces, Mega Tree, RGB</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Add artist and song name tags (e.g., Mariah Carey, All I Want for Christmas)</span>
                    </li>
                  </ul>
                </div>

                {/* Clear Description */}
                <div className="p-6 rounded-lg border bg-card">
                  <div className="flex items-center space-x-3 mb-4">
                    <FileText className="h-6 w-6 text-purple-500" />
                    <h3 className="text-lg font-semibold">✍️ Write a Clear Description</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Mention key layout details (e.g., 16x50 Mega Tree, singing bulbs, P5 matrix)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Highlight what's unique or challenging</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Add timing info if your sequence is aligned to a specific track version</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Community Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Users className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">🙌 Join the Creator Community</h2>
                </div>
              </div>

              <div className="bg-linear-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl p-8 md:p-12 border border-primary/20 text-center">
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-6">
                  At Light Hunt, we're building more than just a marketplace—we're building a creator-powered ecosystem. Every upload helps someone's home come to life with joy, awe, and wonder.
                </p>
                <p className="text-xl font-medium text-foreground mb-8">
                  So keep creating. Keep sharing. And let's light up the season—together.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button asChild size="lg" className="px-8">
                    <Link to="/upload-sequence" className="flex items-center space-x-2">
                      <Upload className="h-5 w-5" />
                      <span>👉 Upload Your First Sequence</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="text-center space-y-6 bg-muted/50 rounded-2xl p-8 md:p-12">
              <div className="flex justify-center">
                <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
              </div>
              
              <h3 className="text-2xl font-bold">Questions or stuck?</h3>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Reach out to us anytime or connect on our community platforms.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button asChild variant="outline" size="lg" className="px-6">
                  <a href="mailto:hello@lightshunt.com" className="flex items-center space-x-2">
                    <Mail className="h-5 w-5" />
                    <span>hello@lightshunt.com</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-6">
                  <Link to="/contact" className="flex items-center space-x-2">
                    <ExternalLink className="h-5 w-5" />
                    <span>Community Platforms</span>
                  </Link>
                </Button>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default SequenceGuide; 